import React, { useState, useMemo } from 'react';
import {
  Box, Typography, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Collapse, Paper, TextField, InputAdornment
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';

/**
 * Reusable expandable table with optional accordion (group-by) mode.
 *
 * Props:
 *   rows: array of row objects (each must have a unique `id`).
 *   columns: [{ key, label, render?, align?, width? }, ...]
 *       - key: data field for default rendering.
 *       - render: optional (row) => ReactNode for custom cells.
 *   renderExpanded: (row) => ReactNode — what shows below the row when expanded.
 *   groupBy: optional { key, label, getTotal, getCount, getStatusSummary }
 *       When provided, a view toggle appears: "By contract" / "By {label}".
 *       getTotal(rows): sum of amount for the group (returns number).
 *       getCount(rows): row count for the group.
 *       getStatusSummary(rows): short string like "3 active · 2 pending".
 *   searchPlaceholder: optional string to enable a top-right search input.
 *   filterRow: optional (row, searchTerm) => boolean.
 *   emptyMessage: shown when no rows.
 */
export default function ExpandableTable({
  rows = [],
  columns = [],
  renderExpanded,
  groupBy,
  searchPlaceholder,
  filterRow,
  emptyMessage = 'No records to show.'
}) {
  const [view, setView] = useState('flat');
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const filteredRows = useMemo(() => {
    if (!search || !filterRow) return rows;
    return rows.filter((r) => filterRow(r, search.toLowerCase()));
  }, [rows, search, filterRow]);

  const groups = useMemo(() => {
    if (view !== 'grouped' || !groupBy) return null;
    const map = new Map();
    filteredRows.forEach((r) => {
      const key = groupBy.keyFn ? groupBy.keyFn(r) : (r[groupBy.key] || 'Unassigned');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries()).map(([key, rs]) => ({ key, rows: rs }));
  }, [filteredRows, view, groupBy]);

  return (
    <Box>
      {/* Toolbar: search + view toggle */}
      {(searchPlaceholder || groupBy) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          {searchPlaceholder && (
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              }}
              sx={{ minWidth: 280, flex: 1, maxWidth: 420 }}
            />
          )}
          {groupBy && (
            <ToggleButtonGroup
              size="small"
              value={view}
              exclusive
              onChange={(_e, v) => v && setView(v)}
            >
              <ToggleButton value="flat" sx={{ textTransform: 'none', px: 2 }}>List</ToggleButton>
              <ToggleButton value="grouped" sx={{ textTransform: 'none', px: 2 }}>By {groupBy.label}</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Table size="small" sx={{ '& th': { fontWeight: 700, color: '#1a237e', background: '#f5f6fa', whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40, p: 1 }} />
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align || 'left'} sx={{ width: c.width, py: 1 }}>{c.label}</TableCell>
              ))}
              <TableCell sx={{ width: '100%', p: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 2} align="center" sx={{ color: '#999', py: 4 }}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}

            {view === 'flat' && filteredRows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow hover selected={!!expanded[row.id]} sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' }, '&.Mui-selected': { background: '#e8eaf6', borderLeft: '4px solid #1a237e', boxShadow: '0 -2px 12px rgba(26,35,126,0.15)', position: 'relative', zIndex: 2 }, '&.Mui-selected:hover': { background: '#dde2f0' } }} onClick={() => toggle(row.id)}>
                  <TableCell sx={{ p: 1 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggle(row.id); }}>
                      {expanded[row.id] ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || 'left'} sx={{ whiteSpace: 'nowrap', py: 0.75 }}>
                      {c.render ? c.render(row) : (row[c.key] ?? '')}
                    </TableCell>
                  ))}
                  <TableCell sx={{ width: '100%', p: 0 }} />
                </TableRow>
                {renderExpanded && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 2} sx={{ py: 0, background: '#fafbfc', borderBottom: expanded[row.id] ? '1px solid #e0e0e0' : 'none', boxShadow: expanded[row.id] ? '0 6px 14px rgba(26,35,126,0.10)' : 'none', position: 'relative', zIndex: 1 }}>
                      <Collapse in={!!expanded[row.id]} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 5 }}>
                          {renderExpanded(row)}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}

            {view === 'grouped' && groups && groups.map((g) => (
              <React.Fragment key={g.key}>
                <TableRow hover sx={{ cursor: 'pointer', background: '#f9fafc', fontWeight: 600 }} onClick={() => toggle('group:' + g.key)}>
                  <TableCell sx={{ p: 1 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggle('group:' + g.key); }}>
                      {expanded['group:' + g.key] ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{g.key}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{groupBy.getCount ? groupBy.getCount(g.rows) : g.rows.length}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {groupBy.getTotal ? groupBy.getTotal(g.rows) : ''}
                  </TableCell>
                  <TableCell colSpan={Math.max(1, columns.length - 3)} sx={{ color: '#666', fontWeight: 400, fontSize: 13 }}>
                    {groupBy.getStatusSummary ? groupBy.getStatusSummary(g.rows) : ''}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={columns.length + 2} sx={{ p: 0, background: '#fafbfc' }}>
                    <Collapse in={!!expanded['group:' + g.key]} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1 }}>
                        <Table size="small">
                          <TableBody>
                            {g.rows.map((row) => (
                              <React.Fragment key={row.id}>
                                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(row.id)}>
                                  <TableCell sx={{ width: 60, pl: 5, p: 1 }}>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggle(row.id); }}>
                                      {expanded[row.id] ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                    </IconButton>
                                  </TableCell>
                                  {columns.map((c) => (
                                    <TableCell key={c.key} align={c.align || 'left'} sx={{ whiteSpace: 'nowrap', width: c.width }}>
                                      {c.render ? c.render(row) : (row[c.key] ?? '')}
                                    </TableCell>
                                  ))}
                                </TableRow>
                                {renderExpanded && (
                                  <TableRow>
                                    <TableCell colSpan={columns.length + 2} sx={{ py: 0, background: '#ffffff', boxShadow: expanded[row.id] ? '0 6px 14px rgba(26,35,126,0.10)' : 'none', position: 'relative', zIndex: 1 }}>
                                      <Collapse in={!!expanded[row.id]} timeout="auto" unmountOnExit>
                                        <Box sx={{ py: 2, px: 5 }}>
                                          {renderExpanded(row)}
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
